import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/form/Select';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import {
  ATTACHMENT_KINDS,
  addInvoiceAttachment,
  getInvoiceAttachments,
  removeInvoiceAttachment,
} from '@/api/salesInvoice';
import { logTime } from '@/pages/accounts/invoiceLogFormat';

const size = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.xls,.xlsx,.doc,.docx,application/pdf,image/*';

/**
 * Files on an invoice — signed copy, proof of delivery, customer PO, other.
 * Adding and removing are written to Invoice Logs; removing needs a reason.
 */
export function InvoiceAttachments({ invoiceId }: { invoiceId: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState(ATTACHMENT_KINDS[0]);
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const q = useQuery({ queryKey: ['invoice-attachments', invoiceId], queryFn: () => getInvoiceAttachments(invoiceId) });
  const list = q.data ?? [];
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['invoice-attachments', invoiceId] });
    qc.invalidateQueries({ queryKey: ['invoice-logs'] });
  };

  const addMut = useMutation({
    mutationFn: () => addInvoiceAttachment(invoiceId, file as File, kind, note),
    onSuccess: (a) => {
      toast.push(`${a.kind} attached`, 'success');
      setFile(null); setNote('');
      if (fileRef.current) fileRef.current.value = '';
      refresh();
    },
    onError: (e) => toast.push(apiError(e, 'Upload failed'), 'error'),
  });
  const removeMut = useMutation({
    mutationFn: (id: number) => removeInvoiceAttachment(invoiceId, id, reason),
    onSuccess: () => { toast.push('Attachment removed', 'warning'); setRemoving(null); setReason(''); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Remove failed'), 'error'),
  });

  return (
    <div>
      <div className="mb-1.5 text-[10px] font-bold uppercase text-[var(--text-muted)]">Attachments ({list.length})</div>

      {list.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {list.map((a) => (
            <li key={a.id} className="rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-bold text-accent">{a.kind}</span>
                <a href={a.url} target="_blank" rel="noreferrer" className="font-bold text-[var(--text-primary)] underline decoration-dotted hover:text-accent">{a.fileName}</a>
                <span className="text-[var(--text-muted)]">{size(a.sizeBytes)}</span>
                <span className="ml-auto text-[var(--text-muted)]">{a.uploadedByName ?? '—'} · {logTime(a.at)}</span>
                <button type="button" className="font-bold text-danger hover:underline" onClick={() => { setRemoving(a.id); setReason(''); }}>Remove</button>
              </div>
              {a.note && <div className="mt-1 text-xs text-[var(--text-secondary)]">{a.note}</div>}
              {removing === a.id && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for removing (kept in Invoice Logs)" className="min-w-[240px] flex-1" />
                  <Button size="sm" variant="glass" onClick={() => setRemoving(null)}>Back</Button>
                  <Button size="sm" variant="danger" disabled={!reason.trim() || removeMut.isPending} onClick={() => removeMut.mutate(a.id)}>Remove file</Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-[var(--border-color)] p-2">
        <div className="w-44 shrink-0">
          <Select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="What the file is">
            {ATTACHMENT_KINDS.map((k) => <option key={k}>{k}</option>)}
          </Select>
        </div>
        <input ref={fileRef} type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="max-w-[230px] text-xs" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="min-w-[160px] flex-1" />
        <Button size="sm" variant="primary" disabled={!file || addMut.isPending} onClick={() => addMut.mutate()}>
          {addMut.isPending ? 'Uploading…' : 'Attach'}
        </Button>
        <span className="w-full text-[10px] text-[var(--text-muted)]">PDF, image, Excel or Word · up to 10 MB · up to 10 files per invoice</span>
      </div>
    </div>
  );
}
